import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

export interface UseUsersByAllocationGroupReturn extends BaseQuery {
  data: components['schemas']['GetUsersByAllocationGroupEndpoint_AllocationGroupUserPagedResponse'] | null;
}

export function useUsersByAllocationGroup(
  allocationGroupId: number | undefined,
  page: number = 1,
  pageSize: number = 10,
): UseUsersByAllocationGroupReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getUsersByAllocationGroup = async () => {
    if (!allocationGroupId || allocationGroupId <= 0 || !organizationId) {
      return {
        items: [],
        totalCount: 0,
        page: 1,
        pageSize: pageSize,
        totalPages: 0,
      };
    }

    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/allocation-groups/{allocationGroupId}/users', {
      params: {
        path: { organizationId, allocationGroupId },
        query: { Page: page, PageSize: pageSize },
      },
    });

    return (
      data ?? {
        items: [],
        totalCount: 0,
        page: 1,
        pageSize,
        totalPages: 0,
      }
    );
  };

  const query = useQuery({
    queryKey: ['usersByAllocationGroup', organizationId, allocationGroupId, page, pageSize],
    queryFn: getUsersByAllocationGroup,
    enabled: !!allocationGroupId && allocationGroupId > 0 && organizationId > 0,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = query;

  return {
    data: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
